/**
 * Global Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs errors, and displays a friendly fallback UI with mascot.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Home, AlertTriangle } from 'lucide-react';

// Import mascot image
const MASCOT_THINKING = '/assets/mascot/sabrina_thinking.png';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a DOM manipulation error that we can recover from
    const isRecoverableError = 
      error?.message?.includes('removeChild') ||
      error?.message?.includes('insertBefore') ||
      error?.message?.includes('appendChild');
    
    if (isRecoverableError) {
      console.warn('Recoverable DOM error detected, attempting reload');
      window.location.reload();
      return { hasError: false, error: null };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div 
          className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full text-center">
            {/* Mascot Image */}
            <div className="relative mb-6">
              <div className="w-48 h-48 mx-auto relative">
                <img 
                  src={MASCOT_THINKING}
                  alt="Sabrina sta pensando"
                  className="w-full h-full object-contain drop-shadow-lg"
                  onError={(e) => {
                    // If image fails, show fallback icon
                    e.target.parentElement.innerHTML = `
                      <div class="w-24 h-24 mx-auto bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center shadow-lg">
                        <svg class="w-12 h-12 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                      </div>
                    `;
                  }}
                />
              </div>
              
              {/* Thought bubble */}
              <div className="absolute top-0 right-1/4 bg-white rounded-2xl px-4 py-2 shadow-lg border border-gray-100 animate-bounce">
                <span className="text-2xl">🤔</span>
              </div>
            </div>
            
            {/* Error Message Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/50">
              <h1 className="text-2xl font-bold text-gray-900 font-['Fredoka'] mb-3">
                Ops! Qualcosa è andato storto
              </h1>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Non preoccuparti, <span className="font-semibold text-violet-600">i tuoi dati sono al sicuro</span>. 
                Ricarica la pagina e tutto tornerà a funzionare! 🔄
              </p>
              
              {/* Error details (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-4 text-left bg-red-50 rounded-lg p-3">
                  <summary className="text-sm font-medium text-red-700 cursor-pointer">
                    Dettagli errore (solo dev)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 overflow-auto max-h-32">
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleReload}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl px-6 py-3 font-semibold shadow-lg shadow-violet-500/30"
                  data-testid="reload-page-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ricarica Pagina
                </Button>
                
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="rounded-xl px-6 py-3"
                  data-testid="go-home-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Torna alla Home
                </Button>
              </div>
            </div>
            
            {/* Footer */}
            <p className="mt-6 text-sm text-gray-500">
              Se il problema persiste, contattaci su{' '}
              <a href="mailto:info@maestrasabrina.it" className="text-violet-600 hover:underline">
                info@maestrasabrina.it
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
